import React, { useMemo } from "react";

import GenericEditorPage from "features/shared/screens/genericEditorPage";
import { Tag, TagType } from "features/shared/types/tag";

import { useDispatch, useSelector } from "services/redux/rootReducer";

import TAG_MODAL_CONFIG from "../constants/modal";
import { initialFields } from "../constants/form";

import { searchArrayOfTags } from "../functions/search";
import { flattenTag, unflattenTag } from "../functions/excel";

import TagForm from "../components/form/tagForm";

import addOneTag from "../actions/set/update/set.addOneTag";
import updateOneTag from "../actions/set/update/set.updateOneTag";
import removeManyTags from "../actions/set/update/set.removeManyTags";
import { verifyForm } from "../functions/form";
import addManyTags from "../actions/set/update/set.addManyTags";
import useImportExport from "features/shared/hooks/useImportExport";
import TAG_TABLE from "../constants/table";

interface Props {
    tagType: TagType;
}

const TagEditorPage: React.FC<Props> = ({ tagType }) => {
    const dispatch = useDispatch();
    const tags = useSelector((state) => state.tags);
    const { sortedData } = useSelector((state) => state.table);

    const routeFilteredTags = useMemo(() => {
        return tags.filter((tag) => tag.key?.startsWith(tagType));
    }, [tags, tagType]);

    const [handleImportData, handleExportData] = useImportExport({
        data: sortedData as Tag[],
        collectionName: "tags",
        exportFileName: "tags",
        unflattenMapperFunction: unflattenTag,
        flattenMapperFunction: flattenTag,
        verificationFunction: async (tag) => {
            return await verifyForm({
                fields: tag,
                tags,
                isUpdating: true,
                isImporting: true,
            });
        },
        onImportSubmitCallback: async (importedData) => {
            dispatch(addManyTags(importedData));
        },
    });

    const handleAddTag = (tag: Tag) => {
        dispatch(addOneTag(tag));
    };

    const handleUpdateTag = (key: string, tag: Tag) => {
        dispatch(
            updateOneTag(key, {
                value: tag.value,
            })
        );
    };

    const handleRemoveTags = (keys: Tag["key"][]) => {
        dispatch(removeManyTags(keys));
    };

    return (
        <GenericEditorPage
            data={routeFilteredTags}
            indexer="key"
            initialForm={initialFields}
            tableDefinition={TAG_TABLE}
            modalConfig={TAG_MODAL_CONFIG}
            form={TagForm}
            onAddCallback={handleAddTag}
            onRawDataSubmitCallback={handleImportData}
            onUpdateCallback={handleUpdateTag}
            onRemoveCallback={handleRemoveTags}
            onExportCallback={handleExportData}
            searchFunction={searchArrayOfTags}
        />
    );
};

export default TagEditorPage;
